/**
 * Navigation Configuration for Header and Footer
 * This file contains all navigation items, links, structure, and action handlers
 * Changes here will reflect across all pages
 */

// UTILITY FUNCTIONS

/**
 * Get HelloTrade base URL from environment variables
 * @returns {string} Base URL for HelloTrade
 */
export function getHTBaseUrl() {
  return process.env.REACT_APP_HT_BASE_URL || "";
}

const baseUrl = getHTBaseUrl();

/**
 * Get the appropriate FCS URL based on environment
 * @returns {string} FCS URL for current environment
 */
const getFcsUrl = () => {
  return process.env.REACT_APP_FCS_URL || "https://stage.hellotrade.com/free-credit-score";
};

export const headerConfig = {
  logo: {
    src: "/logo192.png",
    alt: "HelloTrade Logo",
    link: `${baseUrl}/`,
  },
  navigation: [
    {
      id: "home",
      label: "Home",
      link: "/",
      type: "link",
      visible: true,
      openInNewTab: false, // Opens in same tab
    },
    {
      id: "loans",
      label: "Loans",
      type: "dropdown",
      visible: true,
      items: [
        {
          id: "business-loan",
          label: "Business Loan",
          link: `${baseUrl}/businessloan`,
          visible: true,
          openInNewTab: true, // Opens in same tab
        },
        {
          id: "loan-against-property",
          label: "Loan Against Property",
          link: `${baseUrl}/businessloan`,
          visible: true,
          openInNewTab: true,
        },
        {
          id: "overdraft",
          label: "Overdraft",
          link: `${baseUrl}/businessloan`,
          visible: true,
          openInNewTab: true,
        },
        {
          id: "msme-loan",
          label: "MSME Loan",
          link: `${baseUrl}/businessloan`,
          visible: true,
          openInNewTab: true,
        },
        {
          id: "personal-loan",
          label: "Personal Loan",
          link: `${baseUrl}/personalloan`,
          visible: true,
          openInNewTab: true,
        },
        {
          id: "other-loans",
          label: "Other Loans",
          link: `${baseUrl}/businessloan`,
          visible: true,
          openInNewTab: true,
        },
      ],
    },
    {
      id: "credit-card",
      label: "Credit Card",
      type: "dropdown",
      visible: false,
      items: [
        {
          id: "fd-credit-card",
          label: "FD Credit Card",
          link: `${baseUrl}/credit-cards?card_type=secured`,
          visible: true,
          openInNewTab: true,
        },
        {
          id: "credit-card",
          label: "Credit Card",
          link: `${baseUrl}/credit-cards?card_type=unsecured`,
          visible: true,
          openInNewTab: true,
        },
      ],
    },
    {
      id: "fixed-deposits",
      label: "Fixed Deposits",
      link: `${baseUrl}/fixed-deposit`,
      type: "link",
      visible: false,
      openInNewTab: true, // Opens in new window with UTMs from header
    },
    {
      id: "free-credit-score",
      label: "Free Credit Score",
      link: "/free-credit-score", // Fallback link (not used when action is present)
      type: "link",
      visible: true,
      action: "goToFcs", // Uses goToFcs function for navigation (always opens in new tab)
      openInNewTab: true, // Action handler opens in new tab
    },{
      id: "investor-relations",
      label: "Investor Relations",
      link: "/investor-relations",
      type: "link",
      visible: true,
      openInNewTab: false, // Opens in same tab
    },
  ],
};

export const footerConfig = {
  links: [
    {
      id: "privacy-policy",
      label: "Privacy policy",
      link: "/privacy-policy",
      visible: true,
      openInNewTab: true, // Opens in same tab
    },
    {
      id: "terms",
      label: "Terms",
      link: "/terms-and-conditions",
      visible: true,
      openInNewTab: true,
    },
    {
      id: "lending-partners",
      label: "Lending partners",
      link: "/lending-partners",
      visible: true,
      openInNewTab: true,
    },
  ],
  copyright: "© 2026 IIL Digital Private Limited. All rights reserved.",
};

/**
 * Routes that should NOT display header/footer (Business Loan journey)
 */
export const excludedRoutes = [
  "/businessloan", // BusinessLoan journey handles header/footer conditionally based on displayPage
  "/apply",
  "/downloadReport",
  "/benefit",
];

/**
 * Check if a route should exclude header/footer
 * @param {string} pathname - Current route pathname
 * @returns {boolean} - True if route should be excluded
 */
export const shouldExcludeHeaderFooter = (pathname) => {
  return excludedRoutes.some((route) => pathname.startsWith(route));
};

/**
 * Build URL with UTM parameters
 * @param {string} url - Base URL
 * @param {string} source - UTM source (e.g., 'header_navigation', 'footer_navigation')
 * @param {string} medium - UTM medium (default: 'click')
 * @param {string} campaign - UTM campaign (default: 'hellotrade_common')
 * @returns {string} - URL with UTM parameters
 */
export const buildUrlWithUTM = (url, source = 'header', medium = 'click', campaign = 'hellotrade_common') => {
  try {
    let urlObj;

    // Handle relative URLs
    if (url.startsWith('/')) {
      urlObj = new URL(url, window.location.origin);
      // Preserve existing query params and add UTM params
      urlObj.searchParams.set('utm_source', source);
      urlObj.searchParams.set('utm_medium', medium);
      urlObj.searchParams.set('utm_campaign', campaign);
      // Return relative path with query string
      return urlObj.pathname + urlObj.search;
    }

    // Handle absolute URLs (http/https)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', source);
      urlObj.searchParams.set('utm_medium', medium);
      urlObj.searchParams.set('utm_campaign', campaign);
      return urlObj.toString();
    }

    // Fallback for any other format
    return url;
  } catch (error) {
    console.error('Error building URL with UTM:', error, url);
    return url;
  }
};

// ============================================
// NAVIGATION ACTION HANDLERS
// ============================================

/**
 * Opens the Free Credit Score page in a new tab
 * Uses centralized buildUrlWithUTM for UTM parameter handling
 * @param {Object} utmParams - Optional UTM parameters { source, medium, campaign }
 */
export const handleGoToFcs = (utmParams = null) => {
  let url = getFcsUrl();
  
  // Use centralized UTM builder
  if (utmParams) {
    url = buildUrlWithUTM(
      url, 
      utmParams.source || 'header_navigation',
      utmParams.medium || 'click',
      utmParams.campaign || 'hellotrade_common_header'
    );
  }
  
  window.open(url, "_blank");
};

// ============================================
// NAVIGATION ACTIONS MAP
// ============================================

/**
 * Centralized navigation actions registry
 * Maps action strings to handler functions
 * Only special navigation actions that need custom logic should be registered here
 * For simple links, use the link property with buildUrlWithUTM in CommonHeader
 */
export const navigationActions = {
  goToFcs: handleGoToFcs,
};
